import { useState } from "react";
import { useAppStore } from "@/store";
import {
  SidebarSection,
  SidebarSectionHeader,
  SidebarSectionHeaderSlotName,
  SidebarSectionItem,
  SidebarSectionItemSlotName,
  SidebarSectionSlotName,
  SidebarSectionEmpty,
} from "@/components/layout/components/sidebar/sidebar-section";
import { observer } from "mobx-react-lite";
import { CollectionObservable } from "@/store/collections/CollectionObservable";
import { SectionListTitleWrapper, SeeAllButton } from "./sidebar-contents.style";
import {
  MDSButtonGroup,
  MDSCollectionIcon,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
  MDSTooltip,
  MDSTooltipPlacement,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
  MDSButton,
  MDSIcon,
  MDSButtonVariant,
  MDSIconKind,
  MDSButtonSize,
} from "@/mds";
import { actions } from "@/actions";
import { EventContext } from "@/domains/metrics/context";
import { motion } from "motion/react";

interface SidebarContentsRecentCollectionsListProps {
  onClick?: () => void;
  includeTitle?: boolean;
  indent?: boolean;
}

interface SidebarContentsRecentCollectionsProps {
  onClick?: () => void;
}

export const SidebarContentsRecentCollections = observer(function SidebarContentsRecentCollections({
  onClick,
}: SidebarContentsRecentCollectionsProps) {
  const { store } = useAppStore();
  const [isExpanded, setIsExpanded] = useState(true);
  const hasRecentCollections =
    store.recentItems.sortedRecentItems.filter(item => item instanceof CollectionObservable)
      .length > 0;

  const handleCreateCollection = () => {
    actions.createCollection({
      title: "",
      store,
      eventContext: EventContext.GlobalActions,
      navigateToCollection: true,
      showToast: false,
    });
  };

  return (
    <SidebarSection isExpanded={isExpanded}>
      <SidebarSectionHeader
        slot={SidebarSectionSlotName.Header}
        label="Collections"
        defaultIconKind={MDSIconKind.Collection}
        isExpanded={isExpanded}
        onPress={() => setIsExpanded(!isExpanded)}
      >
        <MDSButtonGroup slot={SidebarSectionHeaderSlotName.ButtonsRight}>
          <MDSTooltip config={{ label: "Create collection", placement: MDSTooltipPlacement.Top }}>
            <MDSIconButton
              size={MDSIconButtonSize.XSmall}
              iconKind={MDSIconKind.Plus}
              variant={MDSIconButtonVariant.OnChrome}
              onClick={handleCreateCollection}
              rounded
            />
          </MDSTooltip>
        </MDSButtonGroup>
      </SidebarSectionHeader>
      {hasRecentCollections && (
        <>
          <SidebarContentsRecentCollectionsList onClick={onClick} indent />
          <SeeAllButton
            onClick={() => {
              onClick?.();
              store.navigation.goToCollections();
            }}
          >
            See All
          </SeeAllButton>
        </>
      )}
      {!hasRecentCollections && (
        <SidebarSectionEmpty>
          <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Tertiary}>
            Use collections to organize your notes in Mem
          </MDSText>
          <MDSButton
            variant={MDSButtonVariant.Subtle}
            size={MDSButtonSize.Small}
            onClick={handleCreateCollection}
          >
            <MDSIcon kind={MDSIconKind.Plus} />
            Create collection
          </MDSButton>
        </SidebarSectionEmpty>
      )}
    </SidebarSection>
  );
});

export const SidebarContentsRecentCollectionsList = observer(
  function SidebarContentsRecentCollectionsList({
    onClick,
    includeTitle,
    indent,
  }: SidebarContentsRecentCollectionsListProps) {
    const { store } = useAppStore();
    const recentCollections = store.recentItems.sortedRecentItems
      .filter(item => item instanceof CollectionObservable)
      .slice(0, 5);

    return (
      <>
        {includeTitle && (
          <SectionListTitleWrapper>
            <MDSText
              variant={MDSTextVariant.TextXs}
              weight={MDSTextWeight.SemiBold}
              color={MDSTextColor.Secondary}
            >
              Recent Collections
            </MDSText>
          </SectionListTitleWrapper>
        )}
        {recentCollections.map(collection => (
          <motion.div key={collection.id} layoutId={collection.id}>
            <SidebarSectionItem
              key={collection.id}
              label={collection.label}
              iconKind={<MDSCollectionIcon size={11} collectionId={collection.id} />}
              onClick={() => {
                onClick?.();
                store.navigation.goToCollection({ collectionId: collection.id });
              }}
              indent={indent}
            >
              <MDSButtonGroup slot={SidebarSectionItemSlotName.ButtonsRight}>
                <MDSTooltip
                  config={{
                    label: collection.isFavorited ? "Unpin from sidebar" : "Pin to sidebar",
                    placement: MDSTooltipPlacement.Top,
                  }}
                >
                  <MDSIconButton
                    size={MDSIconButtonSize.XSmall}
                    iconKind={
                      collection.isFavorited ? MDSIconKind.ThumbtackSolid : MDSIconKind.Thumbtack
                    }
                    variant={MDSIconButtonVariant.OnChrome}
                    onClick={() => {
                      onClick?.();
                      collection.toggleFavorite();
                    }}
                    rounded
                  />
                </MDSTooltip>
              </MDSButtonGroup>
            </SidebarSectionItem>
          </motion.div>
        ))}
      </>
    );
  }
);
